"use client"

import { BlogCategory, BlogsEntry } from "@/api/generated/BMWInterfaces"
import LinkWithParams from "@/lib/common/LinkWithParams"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import { handleShare } from "@/lib/common/utils"
import colors from "@/lib/theme/colors"
import { Button, IconButton, styled, Tooltip, Typography } from "@mui/material"
import Image from "next/image"
import { useState } from "react"
import FAQComp from "../Homepage/FAQComp"
import CustomBreadcrumbs from "../shared/CustomBreadcrumbs"
import BlogCard from "./BlogCard"

const StyledLayout = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 30px auto 48px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0 100px;
    max-width: 1280px;
    margin-top: 6px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    padding: 0 160px;
    max-width: 1920px;
    margin-top: 8px;
  }
`

const StyledContent = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 800px;
    margin: 0 auto;
    padding: 0;
  }
`

const StyledHeader = styled("header")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`

const StyledTitle = styled(Typography)`
  color: ${colors.directBlack};
  text-align: right;
  font-size: 24px;
  font-weight: 400;
  line-height: 35px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 40px;
    line-height: 59px;
  }
`

const StyledSubtitle = styled(Typography)`
  color: ${colors.directBlack};
  text-align: right;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 18px;
    line-height: 26px;
  }
`

const StyledDateBox = styled("div")`
  color: ${colors.darkGrey};
  text-align: right;
  border-top: 1px solid ${colors.platinum};
  border-bottom: 1px solid ${colors.platinum};
  padding: 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 33px;
`

const StyledDate = styled(Typography)`
  color: ${colors.directBlack};
  text-align: right;
  font-size: 18px;
  line-height: 26px;
  font-weight: 400;
`

const StyledShareIconWrapper = styled(IconButton)`
  width: 32px;
  height: 32px;
  border-radius: 0%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 40px;
    height: 40px;
  }
`

const StyledSectionsWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto 48px;
  padding-bottom: 30px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    padding-bottom: 0;
    margin-bottom: 64px;
  }
`

const StyledImageWrapper = styled("div")`
  position: relative;
  width: 100%;
  height: auto;
  border-radius: 16px;
  aspect-ratio: 328/184.5;
  overflow: hidden;
  background-color: ${colors.directVeryLightGray}80; // Placeholder color while image loads

  ${({ theme }) => theme.breakpoints.up("md")} {
    aspect-ratio: 800/450;
  }

  img {
    object-fit: cover;
  }
`

const StyledSection = styled("section")`
  display: flex;
  flex-direction: column;
  gap: 30px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const StyledSectionText = styled("div")`
  color: ${colors.directBlack};
  text-align: right;
  font-family: "Ploni, sans-serif";
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  * {
    margin: 0;
  }

  .table_wrapper {
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    width: 100%;
    table-layout: auto;
  }

  th,
  td {
    padding: 8px 12px;
    text-align: right;
    vertical-align: top;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
  }

  /* DLK-668: header row bold (covers <th> and <thead><td> markup) */
  th,
  thead td {
    font-weight: 700;
  }

  p:last-child,
  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${colors.directBlack};
    font-weight: 400;
    line-height: 1.3;
  }

  h1 {
    font-size: 32px;
  }
  h2 {
    font-size: 28px;
  }
  h3 {
    font-size: 24px;
  }
  h4 {
    font-size: 20px;
  }
  h5 {
    font-size: 18px;
  }
  h6 {
    font-size: 16px;
  }

  ul,
  ol {
    padding-inline-start: 24px;
  }

  strong,
  b {
    font-weight: 700;
  }

  em,
  i {
    font-style: italic;
  }

  a {
    color: ${colors.directBlack};
    text-decoration: underline;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 18px;
    line-height: 26px;

    h1 {
      font-size: 40px;
    }
    h2 {
      font-size: 32px;
    }
    h3 {
      font-size: 28px;
    }
    h4 {
      font-size: 22px;
    }
    h5 {
      font-size: 20px;
    }
    h6 {
      font-size: 18px;
    }
  }
`

const StyledFAQSection = styled("div")`
  display: flex;
  justify-content: center;
  padding: 32px 0;
  margin-bottom: 48px;
  & > section {
    width: 100%;
    padding: 0;
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 40px 0;
    margin-bottom: 64px;
  }
`

const StyledRelatedSection = styled("section")`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 40px;
  width: 100%;
  padding: 0 16px 0 0;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 40px 0;
  }
`

const StyledRelatedTitle = styled(Typography)`
  color: ${colors.directBlack};
  text-align: right;
  font-size: 24px;
  font-weight: 400;
  line-height: normal;
  padding: 0 0 0 16px;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.up("md")} {
    text-align: center;
    font-size: 40px;
    padding: 0;
  }
`

const StyledRelatedGrid = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  padding: 0 0 0 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  & > * {
    scroll-snap-align: start;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    overflow-x: visible;
    scroll-snap-type: none;
    gap: 48px;
    padding: 0;
    justify-content: center;
  }
`

const StyledAllArticlesButton = styled(Button)`
  width: fit-content;
  align-self: flex-start;
  margin: 0 0 0 16px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 300px;
    width: 100%;
    align-self: center;
    margin: 0;
  }
`

interface BlogDetailProps {
  blog: BlogsEntry
  categories?: BlogCategory[]
}

const sanitizeSectionText = (html: string): string => {
  return html
    .replace(/<p>(?:\s|&nbsp;|&#160;|<br\s*\/?\s*>)*<\/p>/gi, "")
    .replace(/<table\b[\s\S]*?<\/table>/gi, (match) => `<div class="table_wrapper">${match}</div>`)
    .trim()
}

const formatBlogDate = (date: string): string => {
  const parsedDate = new Date(date)

  return `${parsedDate.getDate()}/${parsedDate.getMonth() + 1}/${parsedDate.getFullYear()}`
}

const BlogInnerPage = ({ blog, categories = [] }: BlogDetailProps): React.ReactElement => {
  const relatedBlogs = (blog.relatedBlogs ?? []).slice(0, 3) || []
  const [isCopied, setIsCopied] = useState<boolean | null>(null)
  const breadcrumbs = {
    current: { title: blog.title, url: "" },
    parents: [
      { title: "ראשי", url: "/" },
      { title: "בלוג", url: "/blog" },
    ],
  }

  const isCopiedToClipboard = (state: boolean) => {
    setIsCopied(state)
    const timerId = window.setTimeout(() => {
      setIsCopied(null)
    }, 2000)

    return () => {
      window.clearTimeout(timerId)
    }
  }

  return (
    <StyledLayout>
      <Desktop>
        <CustomBreadcrumbs {...breadcrumbs} />
      </Desktop>

      <StyledContent>
        <StyledHeader>
          <StyledTitle variant="h1">{blog.title}</StyledTitle>
          <StyledSubtitle variant="h2">{blog.subTitle}</StyledSubtitle>
        </StyledHeader>
        {blog.dateModified && (
          <StyledDateBox>
            <Desktop>
              <StyledDate variant="caption">
                עודכן בתאריך {formatBlogDate(blog.dateModified)}
              </StyledDate>
            </Desktop>
            <Mobile>
              <StyledDate variant="caption">
                עודכן ב- {formatBlogDate(blog.dateModified)}
              </StyledDate>
            </Mobile>
            <Tooltip
              placement="top"
              open={isCopied !== null}
              title={isCopied ? "הקישור הועתק בהצלחה" : "העתקה נכשלה"}
            >
              <StyledShareIconWrapper
                onClick={() =>
                  handleShare({
                    title: blog.title,
                    text: blog.subTitle,
                    isCopiedToClipboard,
                  })
                }
              >
                <Image src="/assets/share-icon.svg" alt="Share icon" width={24} height={24} />
              </StyledShareIconWrapper>
            </Tooltip>
          </StyledDateBox>
        )}

        <StyledSectionsWrapper>
          {blog.paragraphSectionData && blog.paragraphSectionData.length > 0 && (
            <>
              {blog.paragraphSectionData.map((section, index) => (
                <StyledSection key={index}>
                  {section?.sectionImage
                    ?.filter((image) => image.imageUrl)
                    .map((image, imgIndex) => (
                      <StyledImageWrapper key={imgIndex}>
                        <Image
                          src={image.imageUrl}
                          alt={image.alt || `Section image ${imgIndex + 1}`}
                          fill
                          priority
                        />
                      </StyledImageWrapper>
                    )) || null}
                  {(() => {
                    const sanitizedSectionText = sanitizeSectionText(section.sectionText || "")

                    if (!sanitizedSectionText) {
                      return null
                    }

                    return (
                      <StyledSectionText
                        dangerouslySetInnerHTML={{ __html: sanitizedSectionText }}
                      />
                    )
                  })()}
                </StyledSection>
              ))}
            </>
          )}
        </StyledSectionsWrapper>

        {blog.faq && (
          <StyledFAQSection>
            <FAQComp data={blog.faq} title={blog.faq.title} description={blog.faq.description} />
          </StyledFAQSection>
        )}
      </StyledContent>

      {relatedBlogs?.length > 0 && (
        <StyledRelatedSection>
          <StyledRelatedTitle variant="h2">לעוד כתבות שיכולות לעניין אותך</StyledRelatedTitle>
          <StyledRelatedGrid>
            {relatedBlogs.map((relatedBlog) => {
              const category = categories.find((c) => c.id === relatedBlog.categoryId)?.name
              return (
                <BlogCard key={relatedBlog.id} blog={relatedBlog} category={category} compact />
              )
            })}
          </StyledRelatedGrid>
          <StyledAllArticlesButton
            fullWidth
            variant="contained"
            size="medium"
            LinkComponent={LinkWithParams}
            href="/blog"
          >
            לכל הכתבות
          </StyledAllArticlesButton>
        </StyledRelatedSection>
      )}
    </StyledLayout>
  )
}

export default BlogInnerPage
